import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { patientApi } from "../api/patientApi";
import { medicalsApi } from "../api/medicalsApi";
import { appointmentApi } from "../api/appointmentApi";
import accountApi from "../api/billing/accountApi";
import { generateHumanDisplayName } from "../utils/DataTransformationUtils";
import { useCurrentOrganization } from "../pages/facility/hooks/useOrganization";

export type HistoryEntry = {
  pathname: string;
  search: string;
  label: string;
};

type NavigationHistoryContextType = {
  history: HistoryEntry[];
  setPageLabel: (label: string) => void;
};

const NavigationHistoryContext = createContext<NavigationHistoryContextType>({
  history: [],
  setPageLabel: () => {},
});

const STORAGE_KEY = "nav_history";
const MAX_ENTRIES = 10;
const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;
const isId = (s: string) => OBJECT_ID_RE.test(s);

type EntityInfo =
  | { type: "patient"; id: string; prefix: string }
  | { type: "encounter"; id: string; prefix: string }
  | { type: "appointment"; id: string; prefix: string }
  | { type: "billing-account"; id: string; prefix: string }
  | null;

function extractEntityInfo(pathname: string): EntityInfo {
  const segments = pathname.replace(/^\//, "").split("/");
  const [root, ...rest] = segments;

  if (root === "patients" && isId(rest[0])) {
    return {
      type: "patient",
      id: rest[0],
      prefix: rest[1] === "edit" ? "Edit Patient" : "Patient",
    };
  }

  if (root === "health") {
    // /health/encounters/:id[/plan|/edit|...]
    if (rest[0] === "encounters" && isId(rest[1])) {
      return {
        type: "encounter",
        id: rest[1],
        prefix: rest[2] === "plan" ? "Care Plan" : "Encounter",
      };
    }

    // /health/medication-administrations/:encounterId/view|edit
    if (rest[0] === "medication-administrations" && isId(rest[1])) {
      return {
        type: "encounter",
        id: rest[1],
        prefix: "Medication",
      };
    }

    // /health/attachments/:patientId
    if (rest[0] === "attachments" && isId(rest[1])) {
      return {
        type: "patient",
        id: rest[1],
        prefix: "Attachments",
      };
    }
  }

  // /appointments/:appointmentId
  if (root === "appointments" && isId(rest[0])) {
    return {
      type: "appointment",
      id: rest[0],
      prefix: "Appointment",
    };
  }

  if (root === "billing") {
    // /billing/patient-financials/:accountId
    if (rest[0] === "patient-financials" && isId(rest[1])) {
      return {
        type: "billing-account",
        id: rest[1],
        prefix: "Billing",
      };
    }
    // /billing/account-summary/print/:accountId
    if (rest[0] === "account-summary" && rest[1] === "print" && isId(rest[2])) {
      return {
        type: "billing-account",
        id: rest[2],
        prefix: "Billing",
      };
    }
  }

  return null;
}

function deriveLabel(pathname: string): string {
  const segments = pathname.replace(/^\//, "").split("/");
  const [root, ...rest] = segments;

  switch (root) {
    case "home":
      return rest[0] === "summary" ? "Executive Dashboard" : "Dashboard";
    case "patients":
      if (!rest[0]) return "Patients";
      if (rest[0] === "new") return "New Patient";
      if (isId(rest[0])) return rest[1] === "edit" ? "Edit Patient" : "Patient";
      return "Patients";
    case "health":
      if (rest[0] === "encounters") {
        if (!rest[1]) return "Encounters";
        if (isId(rest[1])) {
          if (rest[2] === "plan") return "Care Plan";
          return "Encounter";
        }
        return "Encounters";
      }
      if (rest[0] === "plans") return "Care Plans";
      if (rest[0] === "prescriptions") return "Prescriptions";
      if (rest[0] === "medication-administrations") {
        if (isId(rest[1])) return "Medication";
        return "Medication Administration";
      }
      if (rest[0] === "triage") return "Triage Board";
      if (rest[0] === "summary") return "Health Summary";
      if (rest[0] === "attachments") {
        if (isId(rest[1])) return "Attachments";
        return "Attachments";
      }
      return "Health";
    case "appointments":
      if (!rest[0]) return "Appointments";
      if (rest[0] === "new") return "New Appointment";
      if (rest[0] === "search") return "Find Appointment";
      if (isId(rest[0])) return "Appointment";
      return "Appointments";
    case "billing":
      if (rest[0] === "patient-financials" && isId(rest[1])) return "Billing";
      if (rest[0] === "invoices" && rest[1] === "form" && isId(rest[2]))
        return "Invoice";
      if (rest[0] === "payments" && rest[1] === "form" && isId(rest[2]))
        return "Payment";
      return "Billing";
    case "staff":
      if (!rest[0]) return "Staff";
      if (rest[0] === "profile") return "My Profile";
      if (isId(rest[0]))
        return rest[1] === "edit" ? "Edit Provider" : "Provider";
      return "Staff";
    case "practice-info":
      if (rest[0] === "edit") return "Edit Practice";
      if (rest[0] === "medical-configuration") return "Medical Configuration";
      return "Practice Info";
    case "facility":
      return "Facility Admin";
    case "communications":
      return "Communications";
    case "settings":
      return "Settings";
    case "notifications":
      return "Notifications";
    default:
      return root
        ? root.charAt(0).toUpperCase() + root.slice(1).replace(/-/g, " ")
        : "Dashboard";
  }
}

function loadHistory(): HistoryEntry[] {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveHistory(history: HistoryEntry[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch {
    // ignore storage errors
  }
}

async function fetchEntityLabel(
  entity: EntityInfo,
  organization?: { id?: string; authId?: string },
): Promise<string | null> {
  if (!entity) return null;

  try {
    if (entity.type === "patient") {
      const patient = await patientApi.getCurrentPatient(entity.id);
      const name = generateHumanDisplayName(patient?.name?.[0]);
      return name ? `${entity.prefix}: ${name}` : null;
    }

    if (entity.type === "encounter") {
      if (!organization?.id || !organization?.authId) return null;
      const encounter = await medicalsApi.getEncounteryId(entity.id, {
        id: organization.id,
        authId: organization.authId,
      });
      const subjectDisplay: string | undefined = encounter?.subject?.display;
      if (subjectDisplay) return `${entity.prefix}: ${subjectDisplay}`;

      const patientId: string | undefined = encounter?.subject?.identifier;
      if (patientId) {
        const patient = await patientApi.getCurrentPatient(patientId);
        const name = generateHumanDisplayName(patient?.name?.[0]);
        return name ? `${entity.prefix}: ${name}` : null;
      }
    }

    if (entity.type === "billing-account") {
      const account = await accountApi.findById(entity.id);
      const subject = account?.subject?.[0];
      const name = subject?.display;
      return name ? `${entity.prefix}: ${name}` : null;
    }

    if (entity.type === "appointment") {
      const appointment = await appointmentApi.getCurrentAppointment(
        entity.id,
        {},
      );
      const patientParticipant = appointment?.participant?.find(
        (p: { actor?: { type?: string; display?: string } }) =>
          p?.actor?.type === "Patient",
      );
      const name = patientParticipant?.actor?.display;
      return name ? `${entity.prefix}: ${name}` : null;
    }
  } catch {
    // silently ignore — generic label remains
  }

  return null;
}

export const NavigationHistoryProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const organization = useCurrentOrganization();
  const [history, setHistory] = useState<HistoryEntry[]>(loadHistory);
  const locationRef = useRef(location);
  locationRef.current = location;

  // Track which pathnames we've already dispatched a fetch for this session.
  // This prevents duplicate concurrent fetches when history re-renders.
  const fetchedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const { pathname, search } = location;

    if (pathname.startsWith("/auth")) return;

    setHistory((prev) => {
      const last = prev[prev.length - 1];
      if (last?.pathname === pathname) return prev;

      // Preserve a previously-enriched label when the user navigates back
      const existing = prev.find((e) => e.pathname === pathname);
      const label = existing?.label ?? deriveLabel(pathname);

      const next = [
        ...prev.filter((e) => e.pathname !== pathname),
        { pathname, search, label },
      ].slice(-MAX_ENTRIES);

      saveHistory(next);
      return next;
    });

    // Kick off an async fetch to enrich the label with the entity name.
    // Skip if we already started a fetch for this pathname this session.
    const entity = extractEntityInfo(pathname);
    if (!entity || fetchedRef.current.has(pathname)) return;
    if (
      entity.type === "encounter" &&
      (!organization?.id || !organization?.authId)
    ) {
      return;
    }
    fetchedRef.current.add(pathname);

    fetchEntityLabel(
      entity,
      organization
        ? { id: organization.id, authId: organization.authId }
        : undefined,
    ).then((enrichedLabel) => {
      if (!enrichedLabel) return;
      setHistory((prev) => {
        const next = prev.map((e) =>
          e.pathname === pathname ? { ...e, label: enrichedLabel } : e,
        );
        saveHistory(next);
        return next;
      });
    });
  }, [location, organization?.id, organization?.authId]);

  const setPageLabel = useCallback((label: string) => {
    const { pathname } = locationRef.current;
    // Mark as fetched so the context doesn't overwrite with a stale API result
    fetchedRef.current.add(pathname);
    setHistory((prev) => {
      const idx = prev.reduce(
        (acc, e, i) => (e.pathname === pathname ? i : acc),
        -1,
      );
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], label };
      saveHistory(next);
      return next;
    });
  }, []);

  return (
    <NavigationHistoryContext.Provider value={{ history, setPageLabel }}>
      {children}
    </NavigationHistoryContext.Provider>
  );
};

export const useNavigationHistory = () => useContext(NavigationHistoryContext);

export const useSetPageLabel = (label: string | null | undefined) => {
  const { setPageLabel } = useNavigationHistory();
  useEffect(() => {
    if (label) setPageLabel(label);
  }, [label, setPageLabel]);
};
